import { useMemo } from "react"
import {
  useMetricDictionaryControllerGetMetricDictionaryQuery,
  useMetricDictionaryControllerGetMetricByKeyQuery,
} from "~/ui-rtk/api/metricDictionaryApi"
import type {
  MetricCategory,
  MetricPlatform,
  ChannelType,
  BusinessArea,
  MetricMetadata,
} from "~/ui-rtk/constants/metric-dictionary"

export type MetricDictionaryFilters = {
  category?: MetricCategory
  platform?: MetricPlatform
  channelType?: ChannelType
  businessArea?: BusinessArea
  search?: string
}

/**
 * Resolve a cube measure member to its dictionary entry. Metrics-cube members
 * are keyed by their bare name (`metrics.spend` -> `spend`); members from other
 * cubes keep their full dotted key (`company_x.foo`). This is the inverse of the
 * backend's `toMeasureKey`, kept in one place so every selector resolves the
 * same way.
 */
export function metricEntryFor(
  metricMap: Map<string, MetricMetadata>,
  member: string,
): MetricMetadata | undefined {
  return (
    metricMap.get(member.replace(/^metrics\./, "")) ?? metricMap.get(member)
  )
}

/**
 * Hook to fetch and manage the metric dictionary
 * Provides the full list of metrics with optional filtering
 */
export function useMetricDictionary(filters?: MetricDictionaryFilters) {
  const { data, isLoading, error, refetch } =
    useMetricDictionaryControllerGetMetricDictionaryQuery(filters || undefined)

  // Create a map for quick lookups by key
  const metricMap = useMemo(() => {
    if (!data?.metrics) return new Map<string, MetricMetadata>()
    return new Map(data.metrics.map(m => [m.key, m]))
  }, [data])

  // Group metrics by category
  const metricsByCategory = useMemo(() => {
    if (!data?.metrics) return new Map<MetricCategory, MetricMetadata[]>()

    const grouped = new Map<MetricCategory, MetricMetadata[]>()
    data.metrics.forEach(metric => {
      metric.category.forEach(cat => {
        const existing = grouped.get(cat) || []
        existing.push(metric)
        grouped.set(cat, existing)
      })
    })
    return grouped
  }, [data])

  // Group metrics by platform
  const metricsByPlatform = useMemo(() => {
    if (!data?.metrics) return new Map<MetricPlatform, MetricMetadata[]>()

    const grouped = new Map<MetricPlatform, MetricMetadata[]>()
    data.metrics.forEach(metric => {
      const existing = grouped.get(metric.platform) || []
      existing.push(metric)
      grouped.set(metric.platform, existing)
    })
    return grouped
  }, [data])

  return {
    metrics: data?.metrics ?? [],
    metricMap,
    metricsByCategory,
    metricsByPlatform,
    totalCount: data?.totalCount ?? 0,
    lastUpdated: data?.lastUpdated,
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook to fetch a single metric's metadata by its key
 * Useful for displaying description/info for a specific metric
 */
export function useMetricDescription(metricKey: string | undefined) {
  const { data, isLoading, error } =
    useMetricDictionaryControllerGetMetricByKeyQuery(
      { key: metricKey ?? "" },
      { skip: !metricKey },
    )

  return {
    metric: data ?? undefined,
    isLoading,
    error,
  }
}

/**
 * Hook to get metric metadata from the cached dictionary
 * More efficient than useMetricDescription when you have multiple metrics
 */
export function useMetricFromDictionary(metricKey: string | undefined) {
  const { metricMap, isLoading } = useMetricDictionary()

  const metric = useMemo(() => {
    if (!metricKey) return undefined
    return metricMap.get(metricKey)
  }, [metricKey, metricMap])

  return {
    metric,
    isLoading,
  }
}

/**
 * Hook to get multiple metrics from the cached dictionary
 */
export function useMetricsFromDictionary(metricKeys: string[]) {
  const { metricMap, isLoading } = useMetricDictionary()

  const metrics = useMemo(
    () =>
      metricKeys
        .map(key => metricMap.get(key))
        .filter((m): m is MetricMetadata => m !== undefined),
    [metricKeys, metricMap],
  )

  return {
    metrics,
    isLoading,
  }
}
